import React, { createElement } from 'react';
import './index.css';
import { render } from 'react-dom';
import { App } from './App';
// ─── Inject critical SEO tags synchronously (before React renders) ───────────
// Title
document.title = 'FluidForm — AI-Native Forms That Feel Human';
// Helper to set/create a meta tag
function setMeta(attr: string, value: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${value}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
setMeta('name', 'description', 'Build intelligent forms in minutes. Let users respond with text, voice, documents, and photos — get clean, validated, schema-aligned data every time. Free to start.');
setMeta('name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large');
setMeta('property', 'og:title', 'FluidForm — AI-Native Forms That Feel Human');
setMeta('property', 'og:description', 'Build intelligent forms in minutes. Let users respond with text, voice, documents, and photos — get clean, validated, schema-aligned data every time.');
setMeta('property', 'og:type', 'website');
setMeta('property', 'og:url', 'https://fluid-form.ai');
setMeta('name', 'twitter:card', 'summary_large_image');
setMeta('name', 'twitter:title', 'FluidForm — AI-Native Forms That Feel Human');
setMeta('name', 'twitter:description', 'Build intelligent forms in minutes. Text, voice, documents, photos — validated and schema-aligned. Free to start.');
// ─── JSON-LD Structured Data ──────────────────────────────────────────────────
const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'SoftwareApplication',
    name: 'FluidForm',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://fluid-form.ai',
    description: 'AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.',
    offers: [{
      '@type': 'Offer',
      name: 'Free Plan',
      price: '0',
      priceCurrency: 'USD',
      description: '1 user, 3 forms, 25 submissions/month, AI form builder'
    }, {
      '@type': 'Offer',
      name: 'Pro Plan',
      price: '39',
      priceCurrency: 'USD',
      description: '3 users, 10 forms, 1,000 submissions/month, advanced AI validations'
    }],
    featureList: ['AI-Enabled Form Creation', 'Multi-Modal Inputs (text, voice, documents, photos)', 'Intelligent Document Inputs', 'Reliable AI Validation', 'Conversational Forms', 'Classic Forms', 'Signature FluidForm experience']
  }, {
    '@type': 'Organization',
    name: 'FluidForm, Inc.',
    url: 'https://fluid-form.ai',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@fluid-form.ai',
      contactType: 'customer support'
    }
  }, {
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: 'What is FluidForm?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FluidForm is an AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.'
      }
    }, {
      '@type': 'Question',
      name: 'How is this different from a chatbot?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Chat UIs can talk, but they're unreliable at producing complete, schema-valid submissions. FluidForm is schema-first: conversation is used to complete and validate a form — then you get clean structured output."
      }
    }, {
      '@type': 'Question',
      name: 'How reliable is the AI?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FluidForm is designed for reliability through schema constraints, validation, and confirmations. You control when AI can auto-fill vs. when it must ask or confirm.'
      }
    }, {
      '@type': 'Question',
      name: 'Can I customize the look and feel?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Use styling controls or custom CSS. Keep full branding and UX control.'
      }
    }, {
      '@type': 'Question',
      name: "What's the relationship between FluidForm and FluidWeb?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FluidForm is the first component on FluidWeb. FluidWeb is the platform for building reliable agentic interfaces beyond forms.'
      }
    }]
  }]
};
const ldScript = document.createElement('script');
ldScript.type = 'application/ld+json';
ldScript.textContent = JSON.stringify(jsonLd);
document.head.appendChild(ldScript);
// ─── Noscript fallback with full page content (indexed by Google) ─────────────
const noscriptContent = `
<div style="font-family:sans-serif;max-width:900px;margin:0 auto;padding:2rem">
  <header>
    <h1>FluidForm — AI-Native Forms That Feel Human</h1>
    <p>Build intelligent forms in minutes. Let users respond with text, voice, documents, and photos — get clean, validated, schema-aligned data every time. Free to start.</p>
    <nav>
      <a href="/#features">Features</a> |
      <a href="/#interfaces">Interfaces</a> |
      <a href="/#integrations">Integrations</a> |
      <a href="/#pricing">Pricing</a> |
      <a href="/#faq">FAQ</a> |
      <a href="/b">Document-Heavy Onboarding</a>
    </nav>
  </header>

  <section id="hero">
    <h2>AI-native Forms — that feel human.</h2>
    <p>Let users respond naturally with text, voice, documents, and photos — and receive clean, validated, schema-aligned data every time.</p>
    <p>Build in minutes, not days. No credit card required. Free to start.</p>
  </section>

  <section id="vertical-b">
    <h2>Loan Apps, Claims & Leases. High-Stakes Forms, Zero Drop-Off.</h2>
    <p>AI-powered forms for loan applications, insurance claims, and leases. Let applicants respond with text, voice, and documents — get complete, accurate submissions every time. Built for finance, insurance & real estate.</p>
    <p>Set up in minutes. No engineering team required.</p>
    <h3>Ready-Made Templates</h3>
    <ul>
      <li><a href="https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/kyc-onboarding">KYC Onboarding</a></li>
      <li><a href="https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/insurance-claim">Insurance Claim</a></li>
      <li><a href="https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/car-loan">Loan Application</a></li>
      <li><a href="https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/onboarding-form">Account Opening</a></li>
      <li><a href="https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/mortgage-application">Mortgage Application</a></li>
    </ul>
    <h3>Features Built for High-Stakes Data Collection</h3>
    <ul>
      <li>Turn Any Document Into a Structured Form — Generate forms from voice, screenshots, or existing PDFs</li>
      <li>Accept Documents, Photos &amp; Voice in a Single Form — Everything maps to your form fields automatically</li>
      <li>Auto-Extract From Any Document — AI pulls the information you need automatically</li>
      <li>AI Validation That Catches What Rules Miss — Catch missing, inconsistent, or suspicious entries</li>
    </ul>
    <h3>Why Teams Choose FluidForm</h3>
    <ul>
      <li>Catches bad data before it becomes your problem — AI validates every submission</li>
      <li>Documents accepted as answers — IDs, bank statements, proof of address uploaded and extracted automatically</li>
      <li>Applicants confirm what matters — Critical fields get explicit confirmation before submission</li>
      <li>Clean data, every time — Every submission comes through complete and consistent</li>
    </ul>
    <p><a href="/b">Learn more about FluidForm for finance, insurance & real estate →</a></p>
  </section>

  <section id="metrics">
    <h2>The FluidForm Difference</h2>
    <ul>
      <li>95% faster form completion by letting users respond the way that's easiest — not forcing rigid inputs.</li>
      <li>30% higher completion rates with conversational, multi-modal form experiences.</li>
      <li>96% higher data accuracy with AI-powered validation of both form data and uploaded documents.</li>
    </ul>
  </section>

  <section id="features">
    <h2>Features That Transform Form Experiences</h2>
    <p>Upgrade conversion and data quality without sacrificing brand control or your existing workflow.</p>

    <article>
      <h3>AI-Enabled Form Creation</h3>
      <p>Create forms in minutes using AI:</p>
      <ul>
        <li>Generate forms from voice, screenshots, or PDFs</li>
        <li>Convert onboarding documents into structured forms</li>
        <li>Automatically style forms to match your brand</li>
      </ul>
    </article>

    <article>
      <h3>Multi-Modal Inputs — In a Single Form</h3>
      <p>Let users respond naturally:</p>
      <ul>
        <li>Type or speak responses</li>
        <li>Upload documents or images</li>
        <li>Capture photos with their camera</li>
        <li>AI maps everything into your schema automatically</li>
      </ul>
    </article>

    <article>
      <h3>Intelligent Document Inputs</h3>
      <p>Documents become structured inputs.</p>
      <ul>
        <li>Upload PDFs, scans, images, or handwritten forms</li>
        <li>AI extracts the fields you care about</li>
        <li>Faster review. Fewer errors. No manual reconciliation.</li>
      </ul>
    </article>

    <article>
      <h3>Reliable AI Validation + Follow-Ups</h3>
      <p>More than required fields — validation that protects your data.</p>
      <ul>
        <li>Catch missing, inconsistent, or suspicious entries</li>
        <li>Ask smart follow-up questions only when needed</li>
      </ul>
    </article>
  </section>

  <section id="interfaces">
    <h2>Adaptable Form Experiences</h2>
    <p>Pick the UX that matches your funnel — without changing the data you collect.</p>
    <article>
      <h3>Signature FluidForm</h3>
      <p>A hybrid chat + form experience that feels human. Users can answer naturally, upload documents, and ask questions — while FluidForm keeps the submission structured and validated.</p>
    </article>
    <article>
      <h3>Classic Forms</h3>
      <p>Single-page or multi-step forms with multi-modal inputs and AI validation built in.</p>
    </article>
    <article>
      <h3>Conversational Forms</h3>
      <p>A chat-first experience that doesn't feel like a form — but still produces complete, schema-valid submissions.</p>
    </article>
  </section>

  <section id="integrations">
    <h2>Embed Anywhere. Route Anywhere.</h2>
    <p>Drop FluidForm into your site or app, then send validated submissions wherever they belong.</p>
    <ul>
      <li><strong>Web &amp; App Embed:</strong> Embed like a normal form. Upgrade UX without changing your data model.</li>
      <li><strong>APIs &amp; Webhooks:</strong> Receive structured submissions in real time. Trigger workflows instantly.</li>
      <li><strong>Custom Integrations:</strong> Start drop-in. Go deeper when you're ready — build exactly what you need.</li>
    </ul>
  </section>

  <section id="pricing">
    <h2>Pricing</h2>
    <article>
      <h3>Free Plan — $0/month</h3>
      <p>For exploration and early adoption</p>
      <ul>
        <li>1 user</li>
        <li>3 forms</li>
        <li>25 submissions/month</li>
        <li>AI form builder</li>
        <li>Limited access to AI features</li>
      </ul>
    </article>
    <article>
      <h3>Pro Plan — $39/month</h3>
      <p>For teams and production workflows</p>
      <ul>
        <li>3 users</li>
        <li>10 forms</li>
        <li>1,000 submissions/month</li>
        <li>Advanced AI validations</li>
        <li>Remove FluidForm branding</li>
      </ul>
    </article>
    <article>
      <h3>Enterprise — Custom pricing</h3>
      <p>For complex workflows and high-touch use cases</p>
      <ul>
        <li>Custom pricing</li>
        <li>Advanced voice &amp; agent capabilities</li>
        <li>Custom integrations</li>
        <li>Priority support &amp; SLA</li>
      </ul>
    </article>
  </section>

  <section id="faq">
    <h2>Frequently Asked Questions</h2>
    <dl>
      <dt>What is FluidForm?</dt>
      <dd>FluidForm is an AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.</dd>
      <dt>How is this different from a chatbot?</dt>
      <dd>Chat UIs can talk, but they're unreliable at producing complete, schema-valid submissions. FluidForm is schema-first: conversation is used to complete and validate a form — then you get clean structured output.</dd>
      <dt>How reliable is the AI?</dt>
      <dd>FluidForm is designed for reliability through schema constraints, validation, and confirmations. You control when AI can auto-fill vs. when it must ask or confirm.</dd>
      <dt>Can I customize the look and feel?</dt>
      <dd>Yes. Use styling controls or custom CSS. Keep full branding and UX control.</dd>
      <dt>Is FluidForm suitable for regulated industries like fintech, insurance, and real estate?</dt>
      <dd>Yes. FluidForm is built schema-first — every submission is validated, structured, and audit-ready. It's designed for workflows where data accuracy and compliance matter.</dd>
      <dt>How does FluidForm handle sensitive documents like IDs and financial statements?</dt>
      <dd>FluidForm extracts and validates document data without storing sensitive files beyond what your configuration allows. You control what gets captured, validated, and passed to your systems.</dd>
      <dt>Can FluidForm integrate with our existing onboarding or CRM tools?</dt>
      <dd>Yes. FluidForm outputs clean, schema-aligned JSON that connects to your existing stack. Custom integrations are available on Pro and Enterprise plans.</dd>
    </dl>
  </section>

  <footer>
    <p>© FluidForm, Inc. All rights reserved.</p>
    <p>2261 Market Street STE 85091 San Francisco, CA 94114</p>
    <p>Email: <a href="mailto:support@fluid-form.ai">support@fluid-form.ai</a></p>
    <nav>
      <a href="/terms">Terms of Use</a> |
      <a href="/privacy">Privacy Policy</a>
    </nav>
  </footer>
</div>
`;
const noscript = document.createElement('noscript');
noscript.innerHTML = noscriptContent;
document.body.insertBefore(noscript, document.body.firstChild);
// ─── React render ─────────────────────────────────────────────────────────────
render(<App data-id="element-488" />, document.getElementById('root'));